// Card type contract (TT-6732 — shared vs personal cards).
//
// Aligned with backend PR #2962:
//   - a card is shared when `card_type === 'budget_card'` (vs 'individual_card');
//   - a shared card carries an updatable `notified_user_id` / `notified_user_email`
//     (the person who receives the card's validation notifications) and stays on
//     the single main `budget_id` server-side.

export const CARD_TYPE = {
  INDIVIDUAL: 'individual_card',
  SHARED: 'budget_card',
} as const;

export type CardType = (typeof CARD_TYPE)[keyof typeof CARD_TYPE];

type MaybeCard = {card_type?: string} | null | undefined;

export function isSharedCard(card: MaybeCard): boolean {
  return (card?.card_type ?? '') === CARD_TYPE.SHARED;
}

export function isSharedType(cardType: string | undefined | null): boolean {
  return (cardType ?? '') === CARD_TYPE.SHARED;
}

// A card keeps occupying its holder until it is archived: REPLACED and TERMINATED
// free them, the three others do not.
const LIVE_CARD_STATUSES = ['UNACTIVATED', 'ACTIVE', 'SUSPENDED'];

export function isLiveCard(card: {status?: string} | null | undefined): boolean {
  return LIVE_CARD_STATUSES.includes(String(card?.status ?? '').toUpperCase());
}

// Short label for badges / lists ("Personnelle" / "Flexible").
export function cardTypeShortLabel(cardType: string | undefined | null): string {
  return isSharedType(cardType) ? 'Flexible' : 'Personnelle';
}

// Long label for the card detail / card visual ("Carte flexible" / "Carte individuelle").
export function cardTypeLongLabel(cardType: string | undefined | null): string {
  return isSharedType(cardType) ? 'Carte flexible' : 'Carte individuelle';
}

// Displayable label for a shared card's notified user (validator). The backend
// exposes `notified_user_email`; fall back to a nested object if present.
export function notifiedUserLabel(card: any): string {
  if (card?.notified_user_email) {
    return card.notified_user_email;
  }
  const u = card?.notified_user ?? {};
  const first = u.firstName ?? u.prenom ?? u.first_name ?? '';
  const last = u.lastName ?? u.nom ?? u.last_name ?? '';
  return `${first} ${last}`.trim() || u.email || '—';
}
